import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AdminPasswordGate from "./components/AdminPasswordGate";
import AdminSigninPage from "./pages/AdminSigninPage";
import AdminHomePage from "./pages/AdminHomePage";
import AdminAlertsPage from "./pages/AdminAlertsPage";
import AdminPricingPage from "./pages/AdminPricingPage";
import AdminExchangeRatesPage from "./pages/AdminExchangeRatesPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminUserDetailPage from "./pages/AdminUserDetailPage";
import AdminAuditPage from "./pages/AdminAuditPage";
import AdminReportsPage from "./pages/AdminReportsPage";
import AdminNewUsersReportPage from "./pages/AdminNewUsersReportPage";
import "./styles-admin.css";

export default function AdminApp() {
  const [currentUser, setCurrentUser] = useState(() => {
    const savedUser = window.localStorage.getItem("jobuwin-current-user");
    if (!savedUser) return null;
    try {
      return JSON.parse(savedUser);
    } catch {
      window.localStorage.removeItem("jobuwin-current-user");
      return null;
    }
  });

  const [authToken, setAuthToken] = useState(() => window.localStorage.getItem("jobuwin-auth-token"));

  useEffect(() => {
    if (currentUser) {
      window.localStorage.setItem("jobuwin-current-user", JSON.stringify(currentUser));
      return;
    }
    window.localStorage.removeItem("jobuwin-current-user");
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setAuthToken(null);
      window.localStorage.removeItem("jobuwin-auth-token");
      return;
    }
    if (authToken) {
      window.localStorage.setItem("jobuwin-auth-token", authToken);
    } else {
      window.localStorage.removeItem("jobuwin-auth-token");
    }
  }, [currentUser, authToken]);

  const handleSignIn = (user, meta) => {
    setCurrentUser(user);
    if (meta && Object.prototype.hasOwnProperty.call(meta, "token")) {
      if (meta.token) setAuthToken(meta.token);
      else setAuthToken(null);
    }
  };

  const handleSignOut = () => {
    setCurrentUser(null);
    setAuthToken(null);
  };

  const adminGuard = (page) =>
    currentUser?.role === "admin" ? page : <Navigate to="/admin/signin" replace />;

  return (
    <Routes>
      <Route element={<AdminPasswordGate />}>
        <Route path="/admin/signin" element={<AdminSigninPage currentUser={currentUser} onSignIn={handleSignIn} />} />
        <Route
          path="/admin"
          element={adminGuard(
            <AdminHomePage currentUser={currentUser} onSignOut={handleSignOut} />
          )}
        />
        <Route
          path="/admin/alerts"
          element={adminGuard(
            <AdminAlertsPage currentUser={currentUser} authToken={authToken} onSignOut={handleSignOut} />
          )}
        />
        <Route
          path="/admin/pricing/exchange-rates"
          element={adminGuard(
            <AdminExchangeRatesPage
              currentUser={currentUser}
              authToken={authToken}
              onSignOut={handleSignOut}
            />
          )}
        />
        <Route
          path="/admin/pricing"
          element={adminGuard(
            <AdminPricingPage
              currentUser={currentUser}
              authToken={authToken}
              onSignOut={handleSignOut}
            />
          )}
        />
        <Route
          path="/admin/reports/new-users"
          element={adminGuard(
            <AdminNewUsersReportPage
              currentUser={currentUser}
              authToken={authToken}
              onSignOut={handleSignOut}
            />
          )}
        />
        <Route
          path="/admin/reports"
          element={adminGuard(
            <AdminReportsPage currentUser={currentUser} onSignOut={handleSignOut} />
          )}
        />
        <Route
          path="/admin/audit"
          element={adminGuard(
            <AdminAuditPage currentUser={currentUser} authToken={authToken} onSignOut={handleSignOut} />
          )}
        />
        <Route
          path="/admin/users/:userId"
          element={adminGuard(
            <AdminUserDetailPage currentUser={currentUser} authToken={authToken} onSignOut={handleSignOut} />
          )}
        />
        <Route
          path="/admin/users"
          element={adminGuard(
            <AdminUsersPage currentUser={currentUser} authToken={authToken} onSignOut={handleSignOut} />
          )}
        />
        <Route path="/" element={<Navigate to="/admin" replace />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>
    </Routes>
  );
}
